import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import {
  Alert,
  Button,
  Card,
  Col,
  Avatar,
  Dropdown,
  Empty,
  Image,
  Input,
  Modal,
  Row,
  Tabs,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined, DownOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import CopyableText from "components/common/CopyableText";
import placeholderPhoto from "assets/img/profileplaceholder.png";
import olreadiaService from "services/olreadiaService";
import { STORAGE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";
import { useUserManagement } from "hooks";
import paths from "routes/paths";

const { Text } = Typography;

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const SafeHtml = ({ value }) => (
  <div
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(String(value || ""), {
        ALLOWED_TAGS: ["p", "br", "b", "strong", "em", "i", "u", "ul", "ol", "li", "span"],
        ALLOWED_ATTR: [],
      }),
    }}
  />
);

const isImageUrl = (url) => /\.(png|jpe?g|webp|gif)(\?|$)/i.test(String(url || ""));
const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(String(url || ""));

const parseMaybeDate = (value) => {
  const d = value ? dayjs(value) : null;
  return d && d.isValid() ? d : null;
};

const FieldTile = ({ label, value, copyable = false, strong = false, span = 1 }) => (
  <div
    style={{
      gridColumn: `span ${span}`,
      border: "1px solid rgba(226, 232, 240, 0.95)",
      borderRadius: 14,
      background: "#fff",
      padding: "12px 14px",
      minHeight: 74,
    }}
  >
    <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>
      {label}
    </div>
    <div
      style={{
        fontSize: 14,
        fontWeight: strong ? 700 : 500,
        color: "#111827",
        lineHeight: 1.5,
        wordBreak: "break-word",
      }}
    >
      {copyable ? (
        <CopyableText value={value} showTooltip={false} stopPropagation={false} />
      ) : (
        value
      )}
    </div>
  </div>
);

const panelCardStyle = {
  borderRadius: 16,
  border: "1px solid rgba(226, 232, 240, 0.9)",
  background: "#fff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
};

const sage = {
  solid: "#5f7d6a",
  solidDark: "#4f6f5f",
  softBg: "rgba(95,125,106,0.10)",
  softBgAlt: "rgba(95,125,106,0.06)",
  border: "rgba(95,125,106,0.18)",
  borderSoft: "rgba(95,125,106,0.14)",
  text: "#4f6f5f",
};

const formatLocationInterest = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const city = value.city || "";
    const state = value.state || "";
    const country = value.country || "";
    const county = value.county || "";
    return [city, state, county, country].filter(Boolean).join(", ");
  }
  return String(value);
};

const normalizeAttachment = (value) => {
  if (!value) return null;
  if (typeof value === "string") return { fileId: value, name: value };
  return value;
};

const extractFileId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    return (
      value.fileId ||
      value.fid ||
      value.file_id ||
      value.id ||
      value.file?.fileId ||
      value.file?.id ||
      null
    );
  }
  return null;
};

const extractFileName = (value, fallback) => {
  if (!value) return fallback || "Attachment";
  // Never surface raw file ids to end-users as the display name.
  if (typeof value === "string") return fallback || "Attachment";
  if (typeof value === "object") {
    return value.name || value.fileName || value.filename || value.originalName || fallback || "Attachment";
  }
  return fallback || "Attachment";
};

const extractFileUrl = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.includes("://")) return value;
  if (typeof value === "object") {
    return (
      value.url ||
      value.uri ||
      value.publicUrl ||
      value.fileUrl ||
      value.downloadUrl ||
      value.signedUrl ||
      value.file?.url ||
      value.file?.uri ||
      ""
    );
  }
  return "";
};

const resolveSignedUrlPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (Array.isArray(payload) && payload.length > 0) return payload[0];
  if (Array.isArray(payload?.signedUrls) && payload.signedUrls.length > 0) return payload.signedUrls[0];
  if (typeof payload?.url === "string" && payload.url.trim()) return payload.url;
  return null;
};

export default function HelperProfessionProfileDetailsPage() {
  const { professionProfileId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { updateHelperProfileStatus } = useUserManagement();
  const [activeSection, setActiveSection] = useState("overview");

  // eslint-disable-next-line no-console
  console.log("[HelperProfessionProfileDetails] route-state", {
    professionProfileId,
    hasProfileState: Boolean(location.state?.profile),
    hasHelperProfileState: Boolean(location.state?.helperProfile),
    seededProfileId: location.state?.profile?.id || null,
    seededProfileName: location.state?.profile?.profileName || location.state?.profile?.userFullName || null,
    helperProfileId: location.state?.helperProfile?.id || null,
    helperProfileEmail: location.state?.helperProfile?.email || location.state?.helperProfile?.userEmail || null,
  });

  const seeded = location?.state?.profile && String(location.state.profile.id) === String(professionProfileId)
    ? location.state.profile
    : null;
  const seededHelperProfile = location?.state?.helperProfile || null;
  const seededDisplay = location?.state?.display || {};

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(seeded);
  const [error, setError] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const [signedUrlByFileId, setSignedUrlByFileId] = useState({});

  const fetchSignedUrlForFileId = async (fileId) => {
    if (!fileId) return null;
    const endpoint = `/storage-service/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(fileId)}`;
    try {
      // eslint-disable-next-line no-console
      console.log("[helper-professions] signed url request", { fileId, endpoint });
      const response = await apiClient.get(endpoint);
      // eslint-disable-next-line no-console
      console.log("[helper-professions] signed url response", { fileId, data: response?.data });
      return resolveSignedUrlPayload(response?.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[helper-professions] signed url fetch failed", {
        fileId,
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
      return null;
    }
  };

  const loadProfile = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!professionProfileId) return;
    // eslint-disable-next-line no-console
    console.log("[HelperProfessionProfileDetails] loadProfile:start", {
      professionProfileId,
      forceRefresh,
      hasSeededProfile: Boolean(seeded),
      seededProfileId: seeded?.id || null,
    });
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line no-console
      console.log("[helper-professions-details] fetch profile", { professionProfileId });
      const res = await olreadiaService.professionProfiles.getAdmin(professionProfileId);
      // eslint-disable-next-line no-console
      console.log("[helper-professions-details] fetch response", res);
      const fullProfile = { ...(seeded || {}), ...(res || {}) };
      setProfile(fullProfile);
      return fullProfile;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[HelperProfessionProfileDetails] loadProfile:error", err);
      setError(err?.message || "Failed to load profession profile");
      setProfile((currentProfile) => currentProfile || seeded || null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [professionProfileId, seeded]);

  useEffect(() => {
    (async () => {
      await loadProfile();
    })();
    return undefined;
  }, [loadProfile]);

  // eslint-disable-next-line no-console
  console.log("[HelperProfessionProfileDetails] render", {
    professionProfileId,
    profileId: profile?.id || null,
    profileName: profile?.profileName || profile?.userFullName || null,
    profileEmail: profile?.userEmail || profile?.email || null,
    loading,
    hasProfile: Boolean(profile),
  });

  const displayName =
    seededHelperProfile?.fullName ||
    [seededHelperProfile?.firstName, seededHelperProfile?.middleName, seededHelperProfile?.lastName]
      .filter(Boolean)
      .join(" ") ||
    seededHelperProfile?.userFullName ||
    seededDisplay?.name ||
    profile?.userFullName ||
    [profile?.firstName, profile?.middleName, profile?.lastName]
      .filter(Boolean)
      .join(" ") ||
    "-";
  const displayEmail =
    seededHelperProfile?.email ||
    seededHelperProfile?.userEmail ||
    seededDisplay?.email ||
    profile?.userEmail ||
    profile?.email ||
    "-";
  const displayPhone =
    seededHelperProfile?.phone ||
    seededHelperProfile?.userPhone ||
    seededDisplay?.phone ||
    profile?.userPhone ||
    profile?.phone ||
    "-";
  const displayProfilePhoto =
    [seededHelperProfile?.profilePhoto, seededDisplay?.photo, profile?.profilePhoto, profile?.photo, profile?.avatar].find(
      (value) => value && String(value).toLowerCase() !== "none",
    ) || placeholderPhoto;
  const counts = useMemo(
    () => ({
      documents: toArray(profile?.documents).length,
      certifications: toArray(profile?.certifications).length,
      references: toArray(profile?.references).length,
      work: toArray(profile?.workExperienceHistory).length,
      education: toArray(profile?.education).length,
      services: toArray(profile?.interestedServices).length,
      locations: toArray(profile?.interestedLocations).length,
      shifts: toArray(profile?.interestedShifts).length,
    }),
    [profile],
  );

  const sectionTabs = useMemo(
    () => [
      { key: "overview", label: "Overview" },
      { key: "background", label: "Background" },
      { key: "documents", label: `Documents (${counts.documents})` },
      { key: "certifications", label: `Certifications (${counts.certifications})` },
      { key: "references", label: `References (${counts.references})` },
      { key: "work", label: `Work History (${counts.work})` },
      { key: "education", label: `Education (${counts.education})` },
      { key: "interests", label: `Interests (${counts.services})` },
    ],
    [counts],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const docIds = [
        ...toArray(profile?.documents).map((d) => extractFileId(d)).filter(Boolean),
        ...toArray(profile?.certifications).map((c) => extractFileId(c?.fileId)).filter(Boolean),
      ]
        .filter(Boolean);

      const unique = [...new Set(docIds)];
      if (!unique.length) return;

      const entries = await Promise.all(unique.map(async (fileId) => [fileId, await fetchSignedUrlForFileId(fileId)]));
      if (cancelled) return;
      const next = {};
      entries.forEach(([fid, url]) => {
        if (url) next[fid] = url;
      });
      setSignedUrlByFileId((prev) => ({ ...prev, ...next }));
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const openPreview = (name, url) => {
    if (!url) return;
    if (!isImageUrl(url) && !isPdfUrl(url)) {
      window.open(url, "_blank", "noreferrer");
      return;
    }
    if (isPdfUrl(url)) {
      window.open(url, "_blank", "noreferrer");
      return;
    }
    setPreviewTitle(name || "Attachment");
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleStatusAction = async (nextStatus, label) => {
    const userId = profile?.userId || profile?.helperId;
    if (!userId) return;
    let notes = null;

    const needsNotes = label === "Reject" || label === "Request update" || label === "Suspend";

    await new Promise((resolve) => {
      let value = "";
      Modal.confirm({
        title: `${label} helper?`,
        icon: <ExclamationCircleOutlined />,
        content: needsNotes ? (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Add a note for the helper/admin log (optional).</Text>
            <div style={{ marginTop: 8 }}>
              <Input.TextArea rows={3} onChange={(e) => (value = e.target.value)} />
            </div>
          </div>
        ) : (
          <Text type="secondary">Are you sure you want to continue?</Text>
        ),
        okText: "Yes",
        cancelText: "No",
        okButtonProps: { danger: label === "Reject" || label === "Suspend" },
        onOk: async () => {
          notes = needsNotes ? value?.trim() || null : null;
          try {
            await updateHelperProfileStatus(userId, nextStatus, notes);
            await loadProfile({ forceRefresh: true });
            message.success(`Helper status updated to ${nextStatus}.`);
            resolve(true);
          } catch (err) {
            message.error(err?.message || "Failed to update helper status");
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  const headerTitle = profile?.profileName || profile?.sector?.name || profile?.sector?.code || "Profession Profile";
  const sectorLabel = profile?.sector?.name || profile?.sector?.code || profile?.sectorCode || "-";
  const actionsMenu = {
    items: [
      {
        key: "approve",
        label: "Approve",
        icon: <CheckOutlined />,
        onClick: () => handleStatusAction("APPROVED", "Approve"),
      },
      {
        key: "verify",
        label: "Verify",
        icon: <CheckOutlined />,
        onClick: () => handleStatusAction("VERIFIED", "Verify"),
      },
      {
        key: "request",
        label: "Request update",
        icon: <ExclamationCircleOutlined />,
        onClick: () => handleStatusAction("PENDING", "Request update"),
      },
      {
        type: "divider",
      },
      {
        key: "suspend",
        label: "Suspend",
        danger: true,
        icon: <CloseOutlined />,
        onClick: () => handleStatusAction("SUSPENDED", "Suspend"),
      },
      {
        key: "reject",
        label: "Reject",
        danger: true,
        icon: <CloseOutlined />,
        onClick: () => handleStatusAction("TERMINATED", "Reject"),
      },
    ],
  };

  const renderOverview = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <Card style={panelCardStyle} title="Profile" styles={{ body: { padding: 16 } }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <FieldTile label="Profile ID" value={profile?.id || "-"} copyable />
            </Col>
            <Col xs={24} md={12}>
              <FieldTile label="User ID" value={profile?.userId || profile?.helperId || "-"} copyable />
            </Col>
            <Col xs={24} md={12}>
              <FieldTile label="Name" value={displayName} strong />
            </Col>
            <Col xs={24} md={12}>
              <FieldTile label="Email" value={displayEmail} />
            </Col>
            <Col xs={24} md={12}>
              <FieldTile label="Phone" value={displayPhone} />
            </Col>
            <Col xs={24} md={12}>
              <FieldTile
                label="Profile Type"
                value={
                  profile?.profileType ? (
                    <Tag
                      style={{
                        marginInlineEnd: 0,
                            background: sage.softBg,
                            color: sage.text,
                            borderColor: sage.border,
                      }}
                    >
                      {String(profile.profileType)}
                    </Tag>
                  ) : (
                    "-"
                  )
                }
              />
            </Col>
            <Col xs={24} md={12}>
              <FieldTile label="Sector" value={sectorLabel || "-"} strong />
            </Col>
            <Col xs={24}>
              <FieldTile
                label="Summary"
                value={profile?.profileSummary ? <SafeHtml value={profile.profileSummary} /> : "-"}
              />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );

  const renderBackground = () => (
    <Card style={panelCardStyle} title="Background" styles={{ body: { padding: 16 } }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <FieldTile label="Experience" value={profile?.experience || "-"} />
        </Col>
        <Col xs={24} md={12}>
          <FieldTile label="Education Level" value={profile?.educationLevel || "-"} />
        </Col>
        <Col xs={24}>
          <FieldTile
            label="Previous Regions"
            value={Array.isArray(profile?.previousRegionsWorkedIn) && profile.previousRegionsWorkedIn.length
              ? profile.previousRegionsWorkedIn.join(", ")
              : "-"}
          />
        </Col>
      </Row>
    </Card>
  );

  const renderDocuments = () => (
    <Card style={panelCardStyle} title="Documents" styles={{ body: { padding: 16 } }}>
      {toArray(profile?.documents).length ? (
        <Row gutter={[12, 12]}>
          {toArray(profile.documents)
            .map(normalizeAttachment)
            .filter(Boolean)
            .map((doc, idx) => {
              const fid = extractFileId(doc);
              const name = doc?.caption || extractFileName(doc, `Document ${idx + 1}`);
              const directUrl = extractFileUrl(doc);
              const url = directUrl || (fid ? (signedUrlByFileId?.[fid] || `${STORAGE_BASE_URL}/files/${fid}`) : "");
              const isImage = isImageUrl(url);
              const key = fid || `${name}-${url}` || name;
              if (!url) return null;
              return (
                <Col key={key} xs={12} sm={8} md={6} lg={6}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 14,
                      border: "1px solid rgba(226, 232, 240, 0.95)",
                    }}
                    styles={{ body: { padding: 10 } }}
                    onClick={() => openPreview(name, url)}
                  >
                    {isImage ? (
                      <Image
                        src={url}
                        alt={name}
                        preview={false}
                        style={{ width: "100%", height: 92, objectFit: "cover", borderRadius: 10 }}
                      />
                    ) : (
                      <div style={{ height: 92, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Tag
                          style={{
                            marginInlineEnd: 0,
                            background: "rgba(37,99,235,0.1)",
                            color: "#1d4ed8",
                            borderColor: "rgba(37,99,235,0.2)",
                          }}
                        >
                          FILE
                        </Tag>
                      </div>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <Text ellipsis={{ tooltip: name }} style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
                        {name}
                      </Text>
                    </div>
                  </Card>
                </Col>
              );
            })}
        </Row>
      ) : (
        <Text type="secondary">No documents uploaded.</Text>
      )}
    </Card>
  );

  const renderCertifications = () => (
    <Card style={panelCardStyle} title="Certifications" styles={{ body: { padding: 16 } }}>
      {Array.isArray(profile?.certifications) && profile.certifications.length ? (
        <Table
          bordered
          rowKey={(r, i) => `${r?.name || "cert"}-${i}`}
          size="small"
          pagination={false}
          tableLayout="auto"
          scroll={{ x: "max-content" }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "-" },
            { title: "Issuer", dataIndex: "issuer", key: "issuer", render: (v) => v || "-" },
            { title: "Cert #", dataIndex: "certificationNumber", key: "certificationNumber", render: (v) => v || "-" },
            {
              title: "Expiry",
              dataIndex: "expiryDate",
              key: "expiryDate",
              render: (v) => {
                const d = parseMaybeDate(v);
                if (!d) return "-";
                const expired = d.isBefore(dayjs());
                return (
                  <Tag
                    style={{
                      marginInlineEnd: 0,
                      background: expired ? "rgba(220,38,38,0.1)" : sage.softBg,
                      color: expired ? "#b91c1c" : sage.text,
                      borderColor: expired ? "rgba(220,38,38,0.18)" : sage.border,
                    }}
                  >
                    {d.format("MMM D, YYYY")}
                  </Tag>
                );
              },
            },
            {
              title: "File",
              key: "file",
              render: (_, r) => {
                const fileId = r?.fileId || null;
                const directUrl = extractFileUrl(r);
                const url = directUrl || (fileId ? (signedUrlByFileId?.[fileId] || `${STORAGE_BASE_URL}/files/${fileId}`) : "");
                if (!url) return "-";
                return (
                  <Button type="link" onClick={() => openPreview(r?.name || "Certification", url)} style={{ paddingInline: 0 }}>
                    Preview
                  </Button>
                );
              },
            },
          ]}
          dataSource={profile.certifications}
        />
      ) : (
        <Text type="secondary">No certifications provided.</Text>
      )}
    </Card>
  );

  const renderReferences = () => (
    <Card style={panelCardStyle} title="References" styles={{ body: { padding: 16 } }}>
      {Array.isArray(profile?.references) && profile.references.length ? (
        <Table
          bordered
          rowKey={(r, i) => `ref-${i}`}
          size="small"
          pagination={false}
          tableLayout="auto"
          scroll={{ x: "max-content" }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "-" },
            { title: "Relationship", dataIndex: "relationship", key: "relationship", render: (v) => v || "-" },
            { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => v || "-" },
            { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "-" },
            { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
          ]}
          dataSource={profile.references}
        />
      ) : (
        <Text type="secondary">No references provided.</Text>
      )}
    </Card>
  );

  const renderWorkHistory = () => (
    <Card style={panelCardStyle} title="Work Experience History" styles={{ body: { padding: 16 } }}>
      {Array.isArray(profile?.workExperienceHistory) && profile.workExperienceHistory.length ? (
        <Table
          bordered
          rowKey={(r, i) => `work-${i}`}
          size="small"
          pagination={false}
          tableLayout="fixed"
          columns={[
            { title: "Organization", dataIndex: "organization", key: "organization", width: "17%", render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
            { title: "Role", dataIndex: "role", key: "role", width: "14%", render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
            { title: "Location", dataIndex: "location", key: "location", width: "14%", render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
            { title: "Start", dataIndex: "startDate", key: "startDate", width: "11%", render: (v) => v || "-" },
            { title: "End", dataIndex: "endDate", key: "endDate", width: "11%", render: (v) => v || "-" },
            {
              title: "Current",
              key: "current",
              width: "9%",
              align: "center",
              render: (_, record) => {
                const isCurrent = record?.current ?? record?.isCurrent;
                return (
                <Tag
                  style={{
                    marginInlineEnd: 0,
                    background: isCurrent ? sage.softBg : "rgba(148,163,184,0.12)",
                    color: isCurrent ? sage.text : "#475569",
                    borderColor: isCurrent ? sage.border : "rgba(148,163,184,0.24)",
                  }}
                >
                  {isCurrent ? "Yes" : "No"}
                </Tag>
                );
              },
            },
            {
              title: "Notes",
              key: "notes",
              width: "24%",
              render: (_, record) => {
                const details = record?.notes;
                return details ? (
                  <Text ellipsis={{ tooltip: details }} style={{ display: "block", width: "100%" }}>
                    {details}
                  </Text>
                ) : "-";
              },
            },
          ]}
          dataSource={profile.workExperienceHistory}
        />
      ) : (
        <Text type="secondary">No work history provided.</Text>
      )}
    </Card>
  );

  const renderEducation = () => (
    <Card style={panelCardStyle} title="Education" styles={{ body: { padding: 16 } }}>
      {Array.isArray(profile?.education) && profile.education.length ? (
        <Table
          bordered
          rowKey={(r, i) => `edu-${i}`}
          size="small"
          pagination={false}
          tableLayout="auto"
          scroll={{ x: "max-content" }}
          columns={[
            { title: "Institution", dataIndex: "institution", key: "institution", render: (v) => v || "-" },
            { title: "Program", dataIndex: "program", key: "program", render: (v) => v || "-" },
            { title: "Level", dataIndex: "educationLevel", key: "educationLevel", render: (v) => v || "-" },
            { title: "Year", dataIndex: "graduationYear", key: "graduationYear", align: "right", render: (v) => v || "-" },
            { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
          ]}
          dataSource={profile.education}
        />
      ) : (
        <Text type="secondary">No education history provided.</Text>
      )}
    </Card>
  );

  const renderInterests = () => (
    <Card style={panelCardStyle} title="Interests" styles={{ body: { padding: 16 } }}>
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <FieldTile
            label="Services"
            value={
              Array.isArray(profile?.interestedServices) && profile.interestedServices.length
                ? profile.interestedServices
                    .map((s) =>
                      typeof s === "string"
                        ? s
                        : s?.briefName || s?.name || s?.code || s?.id,
                    )
                    .filter(Boolean)
                    .join(", ")
                : "-"
            }
          />
        </Col>
        <Col xs={24}>
          <FieldTile
            label="Locations"
            value={
              Array.isArray(profile?.interestedLocations) && profile.interestedLocations.length
                ? profile.interestedLocations.map(formatLocationInterest).filter(Boolean).join(" • ")
                : "-"
            }
          />
        </Col>
        <Col xs={24}>
          <FieldTile
            label="Shift Availability"
            value={
              Array.isArray(profile?.interestedShifts) && profile.interestedShifts.length
                ? profile.interestedShifts.map((s) => String(s)).join(", ")
                : "-"
            }
          />
        </Col>
      </Row>
    </Card>
  );

  return (
    <div className="container-fluid p-3">

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => loadProfile({ forceRefresh: true })}>
              Retry
            </Button>
          }
        />
      ) : null}

      {!profile && !loading ? <Empty description="Profession profile not found" /> : null}

      {profile ? (
        <Space direction="vertical" size={16} style={{ width: "100%", maxWidth: 1280 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", paddingTop: 4 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              >
                Back
              </Button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
              padding: "16px 18px",
              border: "1px solid rgba(226, 232, 240, 0.95)",
              borderRadius: 18,
              background: "#fff",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                minWidth: 0,
                flex: "1 1 420px",
              }}
            >
              <Avatar
                src={displayProfilePhoto}
                size={68}
                shape="circle"
                style={{
                  flex: "0 0 auto",
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                  background: "#f8fafc",
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  Helper profession profile details
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.15,
                    color: "#0f172a",
                    wordBreak: "break-word",
                  }}
                >
                  {headerTitle}
                </div>
              </div>
            </div>

            <Space wrap style={{ marginLeft: "auto", justifyContent: "flex-end" }}>
              <Button
                style={{
                  background: "rgba(255,255,255,0.92)",
                  color: sage.text,
                  borderColor: sage.borderSoft,
                }}
                onClick={() =>
                  navigate(paths.helperProfessionBackgroundChecks, {
                    state: {
                      profile,
                      helperProfile: seededHelperProfile || null,
                      returnTo: location.pathname,
                      display: seededHelperProfile
                        ? {
                            name:
                              seededHelperProfile.fullName ||
                              [seededHelperProfile.firstName, seededHelperProfile.middleName, seededHelperProfile.lastName]
                                .filter(Boolean)
                                .join(" ") ||
                              seededHelperProfile.userFullName ||
                              "-",
                            email: seededHelperProfile.email || seededHelperProfile.userEmail || "-",
                            phone: seededHelperProfile.phone || seededHelperProfile.userPhone || "-",
                            photo: seededHelperProfile.profilePhoto || seededHelperProfile.photo || null,
                          }
                        : undefined,
                    },
                  })
                }
              >
                Background checks
              </Button>
              <Dropdown menu={actionsMenu} trigger={["click"]}>
                <Button
                  type="primary"
                  style={{
                    background: sage.solid,
                    borderColor: sage.solidDark,
                  }}
                >
                  Actions <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </div>

          <div style={{ padding: "10px 24px 0" }}>
              <style>{`
                .helper-profession-tabs .ant-tabs-tab:hover,
                .helper-profession-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn {
                  color: ${sage.text} !important;
                }

                .helper-profession-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                  color: ${sage.text} !important;
                }

                .helper-profession-tabs .ant-tabs-ink-bar {
                  background: ${sage.solid} !important;
                }

                .helper-profession-tabs .ant-tabs-tab {
                  transition: color 0.18s ease, background-color 0.18s ease;
                }
              `}</style>
              <Tabs
                className="helper-profession-tabs"
                activeKey={activeSection}
                onChange={setActiveSection}
                items={sectionTabs}
                tabBarStyle={{ marginBottom: 0 }}
              />
            </div>

          {activeSection === "overview" ? renderOverview() : null}
          {activeSection === "background" ? renderBackground() : null}
          {activeSection === "documents" ? renderDocuments() : null}
          {activeSection === "certifications" ? renderCertifications() : null}
          {activeSection === "references" ? renderReferences() : null}
          {activeSection === "work" ? renderWorkHistory() : null}
          {activeSection === "education" ? renderEducation() : null}
          {activeSection === "interests" ? renderInterests() : null}
        </Space>
      ) : null}

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={900}
        zIndex={100500}
        destroyOnClose
        centered
      >
        {previewUrl ? (
          <div style={{ textAlign: "center" }}>
            <Image
              src={previewUrl}
              alt={previewTitle}
              style={{ maxHeight: "70vh", objectFit: "contain" }}
              preview={false}
            />
          </div>
        ) : (
          <Empty description="No preview available" />
        )}
      </Modal>
    </div>
  );
}
